<script lang="ts" setup>
	import { useI18n } from 'vue-i18n'
	import { RouterLink, useRoute, useRouter } from 'vue-router'
	import { computed, ref } from 'vue'
	import { ChevronDown, Pin, X } from 'lucide-vue-next'
	import { useAuthStore } from '@/stores/auth.store'
	import { useBaseStore } from '@/stores/base.store'
	import { usePinnedStore } from '@/stores/pinned.store'
	import IconMapper from '@/components/layout/IconMapper.vue'
	import MegaNav from '@/components/layout/MegaNav.vue'
	import StatusLight from '@/components/StatusLight.vue'
	import { _translations } from './_translations'

	const { t } = useI18n(_translations)

	const router = useRouter()
	const authStore = useAuthStore()
	const baseStore = useBaseStore()
	const pinnedStore = usePinnedStore()

	// Collapsed state per sidebar section, persisted in localStorage so it
	// survives reloads. Key per section: revodata:nav:collapsed:<name>.
	const COLLAPSED_KEY_PREFIX = 'revodata:nav:collapsed:'
	const readCollapsed = (section: string): boolean => {
		try {
			return localStorage.getItem(COLLAPSED_KEY_PREFIX + section) === '1'
		} catch {
			return false
		}
	}
	const writeCollapsed = (section: string, val: boolean) => {
		try {
			localStorage.setItem(COLLAPSED_KEY_PREFIX + section, val ? '1' : '0')
		} catch {
			// storage disabled — in-memory only
		}
	}
	// Lazy-hydrate each section's collapsed state on first read so we
	// don't have to know the full set of section names ahead of time.
	const collapsedSections = ref<Record<string, boolean>>({})
	const isCollapsed = (section: string): boolean => {
		if (!(section in collapsedSections.value)) {
			collapsedSections.value = {
				...collapsedSections.value,
				[section]: readCollapsed(section),
			}
		}
		return !!collapsedSections.value[section]
	}
	const toggleCollapsed = (section: string) => {
		const next = !isCollapsed(section)
		collapsedSections.value = { ...collapsedSections.value, [section]: next }
		writeCollapsed(section, next)
	}

	type PinnedItem = { kind: 'project' | 'location'; id: string; name: string; projectId?: string }

	const pinnedItems = computed<PinnedItem[]>(() => {
		// Pins sind Admins/Devs vorbehalten — alte localStorage-Pins normaler
		// Nutzer sollen die „Gepinnt"-Sektion nicht einblenden.
		if (!['admin', 'developer'].includes(authStore.role || '')) return []
		const items: PinnedItem[] = []
		const projects = baseStore.megalist?.projects || []
		const projectById = new Map<string, any>()
		for (const p of projects) {
			if (p?.meta?._id) projectById.set(p.meta._id, p)
		}
		// Projects first, then locations — alphabetical within each group.
		const projectEntries: PinnedItem[] = []
		for (const id of pinnedStore.pinnedProjects) {
			const p = projectById.get(id)
			if (!p) continue
			projectEntries.push({
				kind: 'project',
				id,
				name: p.meta?.name || p.meta?.projectNumber || id,
			})
		}
		projectEntries.sort((a, b) => a.name.localeCompare(b.name))
		const locationEntries: PinnedItem[] = []
		for (const id of pinnedStore.pinnedLocations) {
			for (const p of projects) {
				const loc = (p.locations || []).find((l: any) => l?.meta?._id === id)
				if (loc) {
					locationEntries.push({
						kind: 'location',
						id,
						name: loc.meta?.name || id,
						projectId: p.meta?._id,
					})
					break
				}
			}
		}
		locationEntries.sort((a, b) => a.name.localeCompare(b.name))
		items.push(...projectEntries, ...locationEntries)
		return items
	})

	const routes = computed(() => {
		const result: any = {}
		router.options.routes.forEach((el: any) => {
			if (el.meta.group) {
				const allowedRoles: string[] = el.meta?.allowedRoles || []
				const item = { name: el.name, path: el.path, icon: el.meta.icon || null }
				if (allowedRoles.includes(authStore.role)) {
					if (!result[el.meta.group]) result[el.meta.group] = []
					result[el.meta.group].push(item)
				}
			}
		})
		return result
	})
</script>

<template>
	<ul class="rev-menu h-full w-full overflow-y-auto pb-24">
		<!-- //? PINNED -->
		<li v-if="pinnedItems.length" class="py-2">
			<div class="group collapse" :class="isCollapsed('pinned') ? 'collapse-close' : 'collapse-open'">
				<div
					class="collapse-title flex cursor-pointer items-center gap-2"
					@click="toggleCollapsed('pinned')">
					<Pin :size="16" class="text-warning fill-current" />
					{{ t('menu.pinned') }}
					<span class="grow"></span>
					<ChevronDown
						:size="16"
						class="text-base-content/50 transition-transform"
						:class="{ '-rotate-90': isCollapsed('pinned') }" />
				</div>
				<ul class="collapse-content">
					<li v-for="item in pinnedItems" :key="`${item.kind}-${item.id}`" class="flex items-center gap-1">
						<RouterLink
							class="rev-menu-item condensed flex-1 truncate"
							active-class="active"
							:to="
								item.kind === 'project' ? `/projects/${item.id}` : `/location/${item.id}`
							">
							<StatusLight :type="item.kind" :_id="item.id" />
							<span class="truncate">{{ item.name }}</span>
						</RouterLink>
						<button
							type="button"
							class="btn btn-ghost btn-xs btn-circle opacity-50 hover:opacity-100"
							:title="t('unpin')"
							@click.stop="pinnedStore.togglePin(item.kind, item.id)">
							<X :size="14" />
						</button>
					</li>
				</ul>
			</div>
		</li>

		<!-- //? GENERAL ROUTES -->
		<template v-for="(routeItem, key) in routes" :key="key">
			<li class="py-2">
				<div
					class="group collapse"
					:class="isCollapsed(String(key)) ? 'collapse-close' : 'collapse-open'">
					<div
						class="collapse-title flex cursor-pointer items-center gap-2"
						:class="{ 'btn-admin': String(key) === 'admin', 'btn-dev': String(key) === 'developer' }"
						@click="toggleCollapsed(String(key))">
						<IconMapper :icon="routeItem[0].icon" />
						{{ t(`menu.${key}`) || '-' }}
						<span class="grow"></span>
						<ChevronDown
							:size="16"
							class="text-base-content/50 transition-transform"
							:class="{ '-rotate-90': isCollapsed(String(key)) }" />
					</div>

					<ul class="collapse-content">
						<li v-for="route in routeItem" :key="route.path || route.name">
							<RouterLink class="rev-menu-item" active-class="active" v-if="route.path" :to="route.path">
								<IconMapper :icon="route.icon" />
								{{ t(`menu.${route.name}`) }}
							</RouterLink>
						</li>
					</ul>
				</div>
			</li>
		</template>

		<li>
			<MegaNav />
		</li>
	</ul>
</template>

<style scoped>
	@reference '@/styles/tailwind.css';

	/* Active sidebar link. The previous `glass` modifier triggered a
	   backdrop-blur transition that looked jittery when switching between
	   items. Plain background + left-accent stripe + tiny color transition
	   reads cleaner. */
	.active.router-link-exact-active.rev-menu-item {
		@apply bg-primary/15 text-base-content;
		@apply rounded-md;
		@apply relative;
		position: relative;
	}
	.active.router-link-exact-active.rev-menu-item::before {
		content: '';
		position: absolute;
		left: 0;
		top: 4px;
		bottom: 4px;
		width: 3px;
		border-radius: 9999px;
		background-color: hsl(var(--p));
	}
	.rev-menu-item {
		@apply transition-colors duration-150;
	}
</style>
